import produce from 'immer';
import * as _ from 'lodash';

import { errorMessagesListFromResponse } from '../../helpers/errors';
import { createReducer, getErrorStatus, getInitialStatus, getPendingStatus } from '../utils';
import type { SetUserTeamRolesRequest } from '../../../typings/api';
import type { User } from '../../types';

import {
  CREATE_USER,
  DELETE_USER,
  FETCH_ALL_USERS,
  FETCH_ONE_USER,
  SET_USER_TEAM_ROLES,
  UPDATE_USER,
  UPDATE_USER_ENABLED,
  UPDATE_USER_PERMISSION,
} from './actions';
import type { UsersStore } from '.';

const initialState: UsersStore = {
  users: {},
  status: getInitialStatus(),
  updatingUsers: {},
};

export default createReducer(initialState, {
  [`${FETCH_ALL_USERS}_PENDING`]: (state) =>
    produce(state, draftState => {
      draftState.status = getPendingStatus();
    }),
  [`${FETCH_ALL_USERS}_FULFILLED`]: (state, action) =>
    produce(state, draftState => {
      draftState.status = getInitialStatus();
      const users = action.payload.data;
      const usersById = _.keyBy(users, (user) => user.id);
      draftState.users = usersById;
    }),
  [`${FETCH_ALL_USERS}_REJECTED`]: (state, action) =>
    produce(state, draftState => {
      draftState.status = getErrorStatus(action.payload);
    }),

  // fetch one action
  [`${FETCH_ONE_USER}_PENDING`]: state =>
    produce(state, draftState => {
      draftState.status = getPendingStatus();
    }),
  [`${FETCH_ONE_USER}_FULFILLED`]: (state, action) =>
    produce(state, draftState => {
      draftState.status = getInitialStatus();
      draftState.users[action.meta.id] = action.payload.data;
    }),
  [`${FETCH_ONE_USER}_REJECTED`]: (state, action) =>
    produce(state, draftState => {
      draftState.status = getErrorStatus(action.payload);
    }),

  // update action
  [`${UPDATE_USER}_PENDING`]: state =>
    produce(state, draftState => {
      draftState.status = getPendingStatus();
    }),
  [`${UPDATE_USER}_FULFILLED`]: (state, action) =>
    produce(state, draftState => {
      draftState.status = getInitialStatus();
      const current = draftState.users[action.payload.data.id];
      draftState.users[action.payload.data.id] = { ...current, ...action.payload.data };
    }),
  [`${UPDATE_USER}_REJECTED`]: (state, action) =>
    produce(state, draftState => {
      draftState.status = getErrorStatus(action.payload);
    }),

  // delete action
  [`${DELETE_USER}_PENDING`]: state =>
    produce(state, draftState => {
      draftState.status = getPendingStatus();
    }),
  [`${DELETE_USER}_FULFILLED`]: (state, action) =>
    produce(state, draftState => {
      draftState.status = getInitialStatus();
      delete draftState.users[action.meta.userId];
    }),
  [`${DELETE_USER}_REJECTED`]: (state, action) =>
    produce(state, draftState => {
      draftState.status = getErrorStatus(action.payload);
    }),

  // create action
  [`${CREATE_USER}_PENDING`]: state =>
    produce(state, draftState => {
      draftState.status = getPendingStatus();
    }),
  [`${CREATE_USER}_FULFILLED`]: (state, action) =>
    produce(state, draftState => {
      draftState.status = getInitialStatus();
      draftState.users[action.payload.data.id] = action.payload.data;
    }),
  [`${CREATE_USER}_REJECTED`]: (state, action) =>
    produce(state, draftState => {
      draftState.status = getErrorStatus(action.payload);
    }),

  [`${UPDATE_USER_PERMISSION}_PENDING`]: (state) =>
    produce(state, draftState => {
      draftState.status = getPendingStatus();
    }),
  [`${UPDATE_USER_PERMISSION}_FULFILLED`]: (state, action) => {
    const { id } = action.payload.data;
    const userRole = { roleId: action.meta.roleId };
    return produce(state, draftState => {
      draftState.status.loading = false;
      draftState.users[id].userRole = userRole;
      draftState.status = getInitialStatus();
    });
  },
  [`${UPDATE_USER_PERMISSION}_REJECTED`]: (state, action) => {
    return produce(state, draftState => {
      draftState.status = getErrorStatus(action.payload);
    });
  },

  [`${UPDATE_USER_ENABLED}_PENDING`]: (state) =>
    produce(state, draftState => {
      draftState.status = getPendingStatus();
    }),
  [`${UPDATE_USER_ENABLED}_FULFILLED`]: (state, action) =>
    produce(state, draftState => {
      const { id, enabled } = action.payload.data;
      draftState.status.loading = false;
      draftState.users[id].enabled = enabled;
      draftState.status = getInitialStatus();
    }),
  [`${UPDATE_USER_ENABLED}_REJECTED`]: (state, action) =>
    produce(state, draftState => {
      draftState.status = getErrorStatus(action.payload);
    }),

  [`${SET_USER_TEAM_ROLES}_PENDING`]: (state, action) =>
    produce(state, draftState => {
      draftState.updatingUsers[action.meta.userId] = {
        loading: true,
        errors: [],
      };
    }),
  [`${SET_USER_TEAM_ROLES}_FULFILLED`]: (state, action) => {
    const { userId, newRoles }: { userId: number | string; newRoles: SetUserTeamRolesRequest } = action.meta;
    const teamUsers = state.users[userId]?.teamUsers || [];
    // oxlint-disable-next-line oxc/no-map-spread -- copy on purpose: `teamUser` is Redux state
    const newTeamUsers: User['teamUsers'] = teamUsers.map(teamUser => {
      const newRoleId = newRoles.find(({ teamId }) => teamId === teamUser.teamId)?.roleId;
      if (newRoleId) {
        return {
          ...teamUser,
          roleId: newRoleId,
        };
      }
      return teamUser;
    });

    return produce(state, draftState => {
      draftState.updatingUsers[userId] = {
        loading: false,
        errors: [],
      };
      if (draftState.users[userId]) {
        draftState.users[userId].teamUsers = _.unionBy(
          newTeamUsers,
          draftState.users[userId].teamUsers || [],
          teamUser => teamUser.team.id,
        );
      }
    });
  },
  [`${SET_USER_TEAM_ROLES}_REJECTED`]: (state, action) =>
    produce(state, draftState => {
      const { userId } = action.meta;
      draftState.updatingUsers[userId] = {
        loading: false,
        errors: errorMessagesListFromResponse(action.payload),
      };
    }),
});
